import { useEffect, useState } from 'react'
import {
  Link2, Plus, Building2, ChevronRight, Check, X, Clock, AlertTriangle,
  ArrowRight, ArrowLeft, BarChart2, FileText, Shield, Users, Bell,
  CheckCircle, XCircle, Pause, Trash2, Eye, Send, RefreshCw, Search,
  Activity, Database, Lock, Unlock
} from 'lucide-react'
import api from '../../services/api'

const RELATIONSHIP_TYPE_LABELS: Record<string, string> = {
  principal_subcontractor: 'Principal / Subcontractor',
  head_subcontractor: 'Head Contractor / Subcontractor',
  client_provider: 'Client / Service Provider',
  labour_hire: 'Labour Hire / Host Employer',
  custom: 'Custom Relationship',
}

const DATA_TYPE_CONFIG: Record<string, { label: string; icon: any; description: string }> = {
  incidents: { label: 'Incidents', icon: AlertTriangle, description: 'All reportable incidents' },
  near_misses: { label: 'Near Misses', icon: Eye, description: 'Near miss reports' },
  notifiable_incidents: { label: 'Notifiable Incidents', icon: Bell, description: 'Immediate push notification' },
  audits: { label: 'Audits', icon: FileText, description: 'Audit results and findings' },
  permits: { label: 'Permits to Work', icon: Shield, description: 'PTW creation and sign-off' },
  corrective_actions: { label: 'Corrective Actions', icon: CheckCircle, description: 'CAPAs and close-outs' },
  competencies: { label: 'Competencies', icon: Users, description: 'Worker licences and certs' },
  metrics_hours: { label: 'Hours Worked', icon: BarChart2, description: 'For combined TRIFR' },
  safety_alerts: { label: 'Safety Alerts', icon: Bell, description: 'Toolbox talks and alerts' },
  toolbox_talks: { label: 'Toolbox Talks', icon: Users, description: 'Safety briefings' },
}

const SHARING_MODE_CONFIG = {
  mirror: { label: 'Mirror', description: 'Read-only copy in your system', colour: '#2563eb' },
  live_link: { label: 'Live Link', description: 'Both parties see same record', colour: '#16a34a' },
  push_notification: { label: 'Push Only', description: 'Alert only, no copy', colour: '#d97706' },
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', colour: '#d97706', icon: Clock },
  active: { label: 'Active', colour: '#16a34a', icon: CheckCircle },
  suspended: { label: 'Suspended', colour: '#6b7280', icon: Pause },
  terminated: { label: 'Terminated', colour: '#dc2626', icon: XCircle },
}

type Relationship = {
  id: string
  initiating_tenant_id: string
  receiving_tenant_id: string
  relationship_type: string
  status: string
  initiated_at: string
  accepted_at?: string
  notes?: string
  partner: { id: string; name: string; abn?: string; industry?: string }
  rules: SharingRule[]
  last_activity?: string
  i_am_initiator: boolean
}

type SharingRule = {
  id: string
  relationship_id: string
  data_type: string
  direction: string
  sharing_mode: string
  auto_share: number
  scope_sites: string
  include_in_metrics: number
  share_worker_names: number
}

type HoursSubmission = {
  id: string
  period_start: string
  period_end: string
  site_name?: string
  worker_count: number
  total_hours: number
  status: string
  submitting_name: string
  submitted_by_name?: string
}

const DEFAULT_PROPOSED_RULES = [
  { data_type: 'incidents', direction: 'from_receiver', sharing_mode: 'mirror', auto_share: true, include_in_metrics: true, share_worker_names: false, scope_sites: [] },
  { data_type: 'notifiable_incidents', direction: 'from_receiver', sharing_mode: 'push_notification', auto_share: true, include_in_metrics: false, share_worker_names: false, scope_sites: [] },
  { data_type: 'audits', direction: 'from_receiver', sharing_mode: 'mirror', auto_share: true, include_in_metrics: false, share_worker_names: false, scope_sites: [] },
  { data_type: 'metrics_hours', direction: 'from_receiver', sharing_mode: 'mirror', auto_share: true, include_in_metrics: true, share_worker_names: false, scope_sites: [] },
]

export default function RelationshipsPage() {
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [pending, setPending] = useState<Relationship[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'active' | 'pending' | 'invite'>('active')
  const [selected, setSelected] = useState<Relationship | null>(null)
  const [detailTab, setDetailTab] = useState<'rules' | 'records' | 'hours'>('rules')
  const [sharedRecords, setSharedRecords] = useState<any[]>([])
  const [hoursData, setHoursData] = useState<HoursSubmission[]>([])
  const [combinedMetrics, setCombinedMetrics] = useState<any>(null)

  // Invite flow state
  const [inviteStep, setInviteStep] = useState(1)
  const [searchQ, setSearchQ] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [inviteForm, setInviteForm] = useState({
    partner_tenant_id: '',
    partner_name: '',
    relationship_type: 'head_subcontractor',
    notes: '',
    proposed_rules: DEFAULT_PROPOSED_RULES as any[],
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Hours submission
  const [hoursForm, setHoursForm] = useState({ period_start: '', period_end: '', worker_count: '', total_hours: '', site_id: '' })
  const [showHoursForm, setShowHoursForm] = useState(false)

  function load() {
    setLoading(true)
    Promise.all([
      api.get('/relationships'),
      api.get('/relationships/pending'),
    ]).then(([r, p]) => {
      setRelationships(r.data.data || [])
      setPending(p.data.data || [])
    }).catch(console.error).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  useEffect(() => {
    if (selected) {
      if (detailTab === 'records') {
        api.get(`/relationships/${selected.id}/shared-records`).then(r => setSharedRecords(r.data.data || [])).catch(() => {})
      }
      if (detailTab === 'hours') {
        api.get(`/relationships/${selected.id}/hours`).then(r => setHoursData(r.data.data || [])).catch(() => {})
      }
    }
  }, [selected, detailTab])

  useEffect(() => {
    api.get('/relationships/metrics/combined').then(r => setCombinedMetrics(r.data)).catch(() => {})
  }, [])

  async function handleSearch(q: string) {
    setSearchQ(q)
    if (q.length < 2) { setSearchResults([]); return }
    const r = await api.get(`/relationships/search/tenants?q=${encodeURIComponent(q)}`).catch(() => null)
    setSearchResults(r?.data?.data || [])
  }

  async function sendInvite() {
    setSubmitting(true); setError('')
    try {
      await api.post('/relationships/invite', {
        partner_tenant_id: inviteForm.partner_tenant_id,
        relationship_type: inviteForm.relationship_type,
        notes: inviteForm.notes,
        proposed_rules: inviteForm.proposed_rules,
      })
      setTab('active'); setInviteStep(1)
      setInviteForm({ partner_tenant_id: '', partner_name: '', relationship_type: 'head_subcontractor', notes: '', proposed_rules: DEFAULT_PROPOSED_RULES })
      load()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to send invitation')
    } finally { setSubmitting(false) }
  }

  async function accept(rel: Relationship) {
    await api.post(`/relationships/${rel.id}/accept`).catch(() => {})
    load(); setTab('active')
  }

  async function decline(rel: Relationship) {
    if (!confirm('Decline this invitation?')) return
    await api.post(`/relationships/${rel.id}/decline`).catch(() => {})
    load()
  }

  async function suspend(rel: Relationship) {
    if (!confirm('Suspend data sharing with this organisation? They will no longer receive new shared records until reactivated.')) return
    await api.post(`/relationships/${rel.id}/suspend`).catch(() => {})
    load(); setSelected(null)
  }

  async function terminate(rel: Relationship) {
    if (!confirm('Permanently terminate this relationship? This cannot be undone. Existing shared records remain accessible.')) return
    await api.post(`/relationships/${rel.id}/terminate`).catch(() => {})
    load(); setSelected(null)
  }

  async function submitHours() {
    if (!selected) return
    setSubmitting(true)
    try {
      await api.post(`/relationships/${selected.id}/hours`, {
        period_start: hoursForm.period_start,
        period_end: hoursForm.period_end,
        worker_count: parseInt(hoursForm.worker_count) || 0,
        total_hours: parseFloat(hoursForm.total_hours),
        site_id: hoursForm.site_id || undefined,
      })
      setShowHoursForm(false)
      setHoursForm({ period_start: '', period_end: '', worker_count: '', total_hours: '', site_id: '' })
      api.get(`/relationships/${selected.id}/hours`).then(r => setHoursData(r.data.data || [])).catch(() => {})
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to submit hours')
    } finally { setSubmitting(false) }
  }

  function toggleRule(idx: number, field: string, value: any) {
    setInviteForm(f => ({
      ...f,
      proposed_rules: f.proposed_rules.map((r, i) => i === idx ? { ...r, [field]: value } : r),
    }))
  }

  function addRule() {
    setInviteForm(f => ({
      ...f,
      proposed_rules: [...f.proposed_rules, {
        data_type: 'incidents', direction: 'from_receiver', sharing_mode: 'mirror',
        auto_share: true, include_in_metrics: false, share_worker_names: false, scope_sites: [],
      }],
    }))
  }

  function removeRule(idx: number) {
    setInviteForm(f => ({ ...f, proposed_rules: f.proposed_rules.filter((_, i) => i !== idx) }))
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-sm" style={{ color: 'var(--text-muted)' }}>Loading relationships...</div>

  const activeRels = relationships.filter(r => r.status === 'active')
  const inactiveRels = relationships.filter(r => r.status !== 'active' && r.status !== 'pending')

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Cross-Tenant Relationships</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Share safety data with contractors, clients, and partner organisations in real time
          </p>
        </div>
        <button
          onClick={() => { setTab('invite'); setInviteStep(1) }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={16} /> Invite Organisation
        </button>
      </div>

      {/* Combined TRIFR banner */}
      {combinedMetrics && combinedMetrics.linked_partners?.length > 0 && (
        <div className="card border-l-4" style={{ borderLeftColor: 'var(--accent)' }}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>Combined PCBU Metrics</p>
              <div className="flex gap-8 mt-2">
                <div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Own TRIFR</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{combinedMetrics.own?.trifr ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Combined PCBU TRIFR</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{combinedMetrics.combined?.trifr ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Combined LTIFR</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{combinedMetrics.combined?.ltifr ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Total Hours</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{(combinedMetrics.combined?.total_hours || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="text-xs px-2 py-1 rounded-full" style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
              {combinedMetrics.linked_partners?.length} linked org{combinedMetrics.linked_partners?.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}

      {/* Pending invitations alert */}
      {pending.length > 0 && (
        <div className="card" style={{ background: '#fffbeb', border: '1px solid #fcd34d' }}>
          <div className="flex items-center gap-3">
            <Bell size={18} style={{ color: '#d97706' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: '#92400e' }}>
                {pending.length} pending relationship invitation{pending.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs" style={{ color: '#92400e' }}>
                {pending.map(p => p.partner?.name).join(', ')} ha{pending.length === 1 ? 's' : 've'} invited you to share safety data
              </p>
            </div>
            <button className="ml-auto text-xs font-semibold underline" style={{ color: '#d97706' }} onClick={() => setTab('pending')}>
              Review
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
        {[
          { key: 'active', label: `Relationships (${relationships.length})` },
          { key: 'pending', label: `Pending (${pending.length})`, alert: pending.length > 0 },
          { key: 'invite', label: 'Invite Organisation' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors relative ${tab === t.key ? 'border-current' : 'border-transparent'}`}
            style={{ color: tab === t.key ? 'var(--accent)' : 'var(--text-muted)' }}
          >
            {t.label}
            {t.alert && <span className="ml-1 w-2 h-2 rounded-full bg-amber-500 inline-block" />}
          </button>
        ))}
      </div>

      {/* ACTIVE RELATIONSHIPS TAB */}
      {tab === 'active' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Relationship list */}
          <div className="lg:col-span-1 space-y-2">
            {[...activeRels, ...inactiveRels].length === 0 ? (
              <div className="card text-center py-12">
                <Link2 size={32} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>No relationships yet</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Invite a contractor or client organisation to start sharing data</p>
                <button className="btn-primary mt-4 text-xs" onClick={() => setTab('invite')}>
                  <Plus size={14} className="inline mr-1" /> Invite Organisation
                </button>
              </div>
            ) : (
              [...activeRels, ...inactiveRels].map(rel => {
                const S = STATUS_CONFIG[rel.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.terminated
                const SIcon = S.icon
                return (
                  <button
                    key={rel.id}
                    onClick={() => { setSelected(rel); setDetailTab('rules') }}
                    className={`card w-full text-left hover:shadow-md transition-all ${selected?.id === rel.id ? 'ring-2' : ''}`}
                    style={{ ringColor: 'var(--accent)' } as any}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                          style={{ background: 'var(--accent)' }}>
                          {rel.partner?.name?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{rel.partner?.name}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{RELATIONSHIP_TYPE_LABELS[rel.relationship_type]}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ background: S.colour + '20', color: S.colour }}>
                        <SIcon size={10} /> {S.label}
                      </div>
                    </div>
                    <div className="mt-2 flex gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <span>{rel.rules.length} sharing rule{rel.rules.length !== 1 ? 's' : ''}</span>
                      {rel.last_activity && <span>Active {new Date(rel.last_activity).toLocaleDateString('en-AU')}</span>}
                    </div>
                  </button>
                )
              })
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="lg:col-span-2 card space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white"
                      style={{ background: 'var(--accent)' }}>
                      {selected.partner?.name?.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold" style={{ color: 'var(--text)' }}>{selected.partner?.name}</h3>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {RELATIONSHIP_TYPE_LABELS[selected.relationship_type]}
                        {selected.partner?.abn ? ` · ABN ${selected.partner.abn}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selected.status === 'active' && (
                    <>
                      <button className="btn-secondary text-xs py-1" onClick={() => suspend(selected)}>
                        <Pause size={12} className="inline mr-1" /> Suspend
                      </button>
                      <button className="text-xs py-1 px-3 rounded-lg font-medium" style={{ background: '#fef2f2', color: '#dc2626' }}
                        onClick={() => terminate(selected)}>
                        <Trash2 size={12} className="inline mr-1" /> Terminate
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Detail tabs */}
              <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
                {[
                  { key: 'rules', label: 'Sharing Rules' },
                  { key: 'records', label: 'Shared Records' },
                  { key: 'hours', label: 'Hours Submissions' },
                ].map(t => (
                  <button
                    key={t.key}
                    onClick={() => setDetailTab(t.key as any)}
                    className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${detailTab === t.key ? 'border-current' : 'border-transparent'}`}
                    style={{ color: detailTab === t.key ? 'var(--accent)' : 'var(--text-muted)' }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Rules tab */}
              {detailTab === 'rules' && (
                <div className="space-y-2">
                  {selected.rules.length === 0 ? (
                    <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No sharing rules configured</p>
                  ) : (
                    selected.rules.map(rule => {
                      const dt = DATA_TYPE_CONFIG[rule.data_type]
                      const mode = SHARING_MODE_CONFIG[rule.sharing_mode as keyof typeof SHARING_MODE_CONFIG]
                      const DIcon = dt?.icon || Database
                      const isInbound = rule.direction === 'from_receiver'
                        ? !selected.i_am_initiator
                        : selected.i_am_initiator

                      return (
                        <div key={rule.id} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--surface-raised)' }}>
                          <DIcon size={16} style={{ color: 'var(--accent)' }} />
                          <div className="flex-1">
                            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{dt?.label || rule.data_type}</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{dt?.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {isInbound
                              ? <><ArrowLeft size={12} style={{ color: '#16a34a' }} /><span className="text-xs" style={{ color: '#16a34a' }}>Receiving</span></>
                              : <><ArrowRight size={12} style={{ color: '#2563eb' }} /><span className="text-xs" style={{ color: '#2563eb' }}>Sending</span></>
                            }
                          </div>
                          <div className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{ background: mode?.colour + '20', color: mode?.colour }}>
                            {mode?.label}
                          </div>
                          {rule.include_in_metrics === 1 && (
                            <div className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{ background: '#f0fdf4', color: '#16a34a' }}>
                              In TRIFR
                            </div>
                          )}
                        </div>
                      )
                    })
                  )}
                  {selected.notes && (
                    <p className="text-xs p-2 rounded" style={{ background: 'var(--surface-raised)', color: 'var(--text-muted)' }}>
                      Note: {selected.notes}
                    </p>
                  )}
                </div>
              )}

              {/* Shared records tab */}
              {detailTab === 'records' && (
                <div className="space-y-2">
                  {sharedRecords.length === 0 ? (
                    <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No shared records yet</p>
                  ) : (
                    sharedRecords.map(rec => {
                      const isInbound = rec.destination_tenant_id !== rec.source_tenant_id
                      const meta = (() => { try { return JSON.parse(rec.metadata || '{}') } catch { return {} } })()
                      return (
                        <div key={rec.id} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'var(--surface-raised)' }}>
                          <div className={`mt-0.5 px-2 py-0.5 rounded text-xs font-medium ${rec.source_tenant_id !== selected.partner?.id ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                            {rec.source_tenant_id !== selected.partner?.id ? '↑ Sent' : '↓ Received'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                              {rec.source_record_type.replace('_', ' ')}
                              {meta.clean_record?.incident_number ? ` · ${meta.clean_record.incident_number}` : ''}
                            </p>
                            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                              {meta.clean_record?.description?.slice(0, 100) || 'No description'}
                            </p>
                          </div>
                          <div className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>
                            {new Date(rec.shared_at).toLocaleDateString('en-AU')}
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              )}

              {/* Hours tab */}
              {detailTab === 'hours' && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Hours Submissions</p>
                    <button className="btn-secondary text-xs py-1" onClick={() => setShowHoursForm(true)}>
                      <Plus size={12} className="inline mr-1" /> Submit Hours
                    </button>
                  </div>

                  {showHoursForm && (
                    <div className="p-4 rounded-lg space-y-3" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)' }}>
                      <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Submit Hours Worked</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="label">Period Start</label>
                          <input type="date" className="input" value={hoursForm.period_start}
                            onChange={e => setHoursForm(f => ({ ...f, period_start: e.target.value }))} />
                        </div>
                        <div>
                          <label className="label">Period End</label>
                          <input type="date" className="input" value={hoursForm.period_end}
                            onChange={e => setHoursForm(f => ({ ...f, period_end: e.target.value }))} />
                        </div>
                        <div>
                          <label className="label">Worker Count</label>
                          <input type="number" className="input" placeholder="0" value={hoursForm.worker_count}
                            onChange={e => setHoursForm(f => ({ ...f, worker_count: e.target.value }))} />
                        </div>
                        <div>
                          <label className="label">Total Hours</label>
                          <input type="number" className="input" placeholder="0" value={hoursForm.total_hours}
                            onChange={e => setHoursForm(f => ({ ...f, total_hours: e.target.value }))} />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className="btn-primary text-xs" onClick={submitHours} disabled={submitting}>Submit</button>
                        <button className="btn-secondary text-xs" onClick={() => setShowHoursForm(false)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {hoursData.length === 0 ? (
                    <p className="text-sm text-center py-6" style={{ color: 'var(--text-muted)' }}>No hours submitted yet</p>
                  ) : (
                    <div className="overflow-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ color: 'var(--text-muted)' }}>
                            <th className="text-left py-2 font-medium">Period</th>
                            <th className="text-left py-2 font-medium">Workers</th>
                            <th className="text-left py-2 font-medium">Hours</th>
                            <th className="text-left py-2 font-medium">From</th>
                            <th className="text-left py-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hoursData.map(sub => (
                            <tr key={sub.id} style={{ borderTop: '1px solid var(--border)' }}>
                              <td className="py-2">{sub.period_start} – {sub.period_end}</td>
                              <td className="py-2">{sub.worker_count}</td>
                              <td className="py-2 font-semibold">{sub.total_hours.toLocaleString()}</td>
                              <td className="py-2">{sub.submitting_name}</td>
                              <td className="py-2">
                                <span className={`px-2 py-0.5 rounded-full font-medium ${
                                  sub.status === 'approved' ? 'bg-green-50 text-green-700' :
                                  sub.status === 'rejected' ? 'bg-red-50 text-red-700' :
                                  'bg-amber-50 text-amber-700'}`}>
                                  {sub.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* PENDING TAB */}
      {tab === 'pending' && (
        <div className="space-y-4">
          {pending.length === 0 ? (
            <div className="card text-center py-12">
              <Check size={32} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No pending invitations</p>
            </div>
          ) : (
            pending.map(rel => (
              <div key={rel.id} className="card space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>
                      Invitation from
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white"
                        style={{ background: 'var(--accent)' }}>
                        {rel.partner?.name?.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold" style={{ color: 'var(--text)' }}>{rel.partner?.name}</h3>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {RELATIONSHIP_TYPE_LABELS[rel.relationship_type]}
                          {rel.partner?.industry ? ` · ${rel.partner.industry}` : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-primary" onClick={() => accept(rel)}>
                      <Check size={14} className="inline mr-1" /> Accept
                    </button>
                    <button className="btn-secondary" onClick={() => decline(rel)}>
                      <X size={14} className="inline mr-1" /> Decline
                    </button>
                  </div>
                </div>

                {rel.notes && (
                  <div className="p-3 rounded-lg text-sm" style={{ background: 'var(--surface-raised)', color: 'var(--text)' }}>
                    "{rel.notes}"
                  </div>
                )}

                {rel.rules.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>
                      PROPOSED SHARING RULES
                    </p>
                    <div className="space-y-1">
                      {rel.rules.map(rule => {
                        const dt = DATA_TYPE_CONFIG[rule.data_type]
                        const mode = SHARING_MODE_CONFIG[rule.sharing_mode as keyof typeof SHARING_MODE_CONFIG]
                        const DIcon = dt?.icon || Database
                        return (
                          <div key={rule.id} className="flex items-center gap-2 p-2 rounded" style={{ background: 'var(--surface-raised)' }}>
                            <DIcon size={14} style={{ color: 'var(--accent)' }} />
                            <span className="text-xs font-medium flex-1" style={{ color: 'var(--text)' }}>{dt?.label}</span>
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {rule.direction === 'from_receiver' ? '→ They send to you' : '← You send to them'}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full"
                              style={{ background: mode?.colour + '20', color: mode?.colour }}>
                              {mode?.label}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* INVITE TAB */}
      {tab === 'invite' && (
        <div className="max-w-2xl">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3, 4].map(n => (
              <div key={n} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${inviteStep >= n ? 'text-white' : ''}`}
                  style={{ background: inviteStep >= n ? 'var(--accent)' : 'var(--border)', color: inviteStep < n ? 'var(--text-muted)' : 'white' }}>
                  {inviteStep > n ? <Check size={12} /> : n}
                </div>
                {n < 4 && <div className="w-8 h-0.5 rounded" style={{ background: inviteStep > n ? 'var(--accent)' : 'var(--border)' }} />}
              </div>
            ))}
            <p className="ml-2 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
              {['Find organisation', 'Relationship type', 'What you receive', 'Review & send'][inviteStep - 1]}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: '#fef2f2', color: '#dc2626' }}>
              {error}
            </div>
          )}

          {/* Step 1: Find org */}
          {inviteStep === 1 && (
            <div className="card space-y-4">
              <h3 className="font-semibold" style={{ color: 'var(--text)' }}>Find organisation on HSEQ Intelligence</h3>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-3" style={{ color: 'var(--text-muted)' }} />
                <input
                  className="input pl-9"
                  placeholder="Search by company name or ABN..."
                  value={searchQ}
                  onChange={e => handleSearch(e.target.value)}
                />
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-1">
                  {searchResults.map(t => (
                    <button key={t.id}
                      onClick={() => { setInviteForm(f => ({ ...f, partner_tenant_id: t.id, partner_name: t.name })); setSearchResults([]) }}
                      className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      style={{ border: inviteForm.partner_tenant_id === t.id ? '2px solid var(--accent)' : '1px solid var(--border)' }}>
                      <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{t.name}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {t.abn ? `ABN ${t.abn}` : 'No ABN'} · {t.industry || 'Unknown industry'}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {inviteForm.partner_tenant_id && (
                <div className="p-3 rounded-lg" style={{ background: 'var(--surface-raised)', border: '1px solid var(--accent)' }}>
                  <div className="flex items-center gap-2">
                    <Check size={14} style={{ color: 'var(--accent)' }} />
                    <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Selected: {inviteForm.partner_name}</p>
                  </div>
                </div>
              )}
              <button className="btn-primary w-full" onClick={() => setInviteStep(2)} disabled={!inviteForm.partner_tenant_id}>
                Next <ChevronRight size={14} className="inline" />
              </button>
            </div>
          )}

          {/* Step 2: Relationship type */}
          {inviteStep === 2 && (
            <div className="card space-y-4">
              <h3 className="font-semibold" style={{ color: 'var(--text)' }}>Relationship type with {inviteForm.partner_name}</h3>
              <div className="space-y-2">
                {Object.entries(RELATIONSHIP_TYPE_LABELS).map(([key, label]) => (
                  <button key={key}
                    onClick={() => setInviteForm(f => ({ ...f, relationship_type: key }))}
                    className="w-full text-left p-3 rounded-lg transition-colors"
                    style={{ border: inviteForm.relationship_type === key ? '2px solid var(--accent)' : '1px solid var(--border)' }}>
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{label}</p>
                  </button>
                ))}
              </div>
              <div>
                <label className="label">Note to partner (optional)</label>
                <textarea className="input" rows={3} placeholder="Context for this relationship..."
                  value={inviteForm.notes} onChange={e => setInviteForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="flex gap-2">
                <button className="btn-secondary flex-1" onClick={() => setInviteStep(1)}>Back</button>
                <button className="btn-primary flex-1" onClick={() => setInviteStep(3)}>Next</button>
              </div>
            </div>
          )}

          {/* Step 3: Sharing rules */}
          {inviteStep === 3 && (
            <div className="card space-y-4">
              <h3 className="font-semibold" style={{ color: 'var(--text)' }}>Configure data sharing rules</h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Define what data flows between your organisation and {inviteForm.partner_name}. Both parties must agree.
              </p>

              <div className="space-y-3">
                {inviteForm.proposed_rules.map((rule, idx) => {
                  const dt = DATA_TYPE_CONFIG[rule.data_type]
                  const DIcon = dt?.icon || Database
                  return (
                    <div key={idx} className="p-3 rounded-lg space-y-2" style={{ border: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-2">
                        <DIcon size={14} style={{ color: 'var(--accent)' }} />
                        <select className="input flex-1 py-1 text-sm"
                          value={rule.data_type}
                          onChange={e => toggleRule(idx, 'data_type', e.target.value)}>
                          {Object.entries(DATA_TYPE_CONFIG).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                        <button onClick={() => removeRule(idx)} className="text-red-400 hover:text-red-600">
                          <X size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Direction</label>
                          <select className="input py-1 text-xs" value={rule.direction}
                            onChange={e => toggleRule(idx, 'direction', e.target.value)}>
                            <option value="from_receiver">They → Us</option>
                            <option value="from_initiator">Us → Them</option>
                            <option value="both">Both ways</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Mode</label>
                          <select className="input py-1 text-xs" value={rule.sharing_mode}
                            onChange={e => toggleRule(idx, 'sharing_mode', e.target.value)}>
                            {Object.entries(SHARING_MODE_CONFIG).map(([k, v]) => (
                              <option key={k} value={k}>{v.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="flex items-center gap-1 cursor-pointer text-xs" style={{ color: 'var(--text-muted)' }}>
                            <input type="checkbox" checked={rule.include_in_metrics}
                              onChange={e => toggleRule(idx, 'include_in_metrics', e.target.checked)} />
                            Include in TRIFR
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer text-xs" style={{ color: 'var(--text-muted)' }}>
                            <input type="checkbox" checked={rule.share_worker_names}
                              onChange={e => toggleRule(idx, 'share_worker_names', e.target.checked)} />
                            Share worker names
                          </label>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <button className="btn-secondary w-full text-sm" onClick={addRule}>
                <Plus size={14} className="inline mr-1" /> Add sharing rule
              </button>

              <div className="flex gap-2">
                <button className="btn-secondary flex-1" onClick={() => setInviteStep(2)}>Back</button>
                <button className="btn-primary flex-1" onClick={() => setInviteStep(4)}>Review</button>
              </div>
            </div>
          )}

          {/* Step 4: Review & send */}
          {inviteStep === 4 && (
            <div className="card space-y-4">
              <h3 className="font-semibold" style={{ color: 'var(--text)' }}>Review invitation</h3>

              <div className="p-3 rounded-lg space-y-1" style={{ background: 'var(--surface-raised)' }}>
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--text-muted)' }}>Partner</span>
                  <span className="font-medium" style={{ color: 'var(--text)' }}>{inviteForm.partner_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--text-muted)' }}>Relationship type</span>
                  <span className="font-medium" style={{ color: 'var(--text)' }}>{RELATIONSHIP_TYPE_LABELS[inviteForm.relationship_type]}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--text-muted)' }}>Sharing rules</span>
                  <span className="font-medium" style={{ color: 'var(--text)' }}>{inviteForm.proposed_rules.length} rules</span>
                </div>
              </div>

              <div className="space-y-1">
                {inviteForm.proposed_rules.map((rule, idx) => {
                  const dt = DATA_TYPE_CONFIG[rule.data_type]
                  const mode = SHARING_MODE_CONFIG[rule.sharing_mode as keyof typeof SHARING_MODE_CONFIG]
                  return (
                    <div key={idx} className="flex items-center gap-2 text-xs p-2 rounded" style={{ background: 'var(--surface-raised)' }}>
                      <span className="font-medium flex-1" style={{ color: 'var(--text)' }}>{dt?.label}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{rule.direction === 'from_receiver' ? 'Inbound' : rule.direction === 'from_initiator' ? 'Outbound' : 'Both'}</span>
                      <span className="px-1.5 py-0.5 rounded-full" style={{ background: mode?.colour + '20', color: mode?.colour }}>{mode?.label}</span>
                    </div>
                  )
                })}
              </div>

              <div className="p-3 rounded-lg text-xs" style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                <Lock size={12} className="inline mr-1" />
                {inviteForm.partner_name} must accept this invitation before any data is shared. Both parties can revoke access at any time.
              </div>

              <div className="flex gap-2">
                <button className="btn-secondary flex-1" onClick={() => setInviteStep(3)}>Back</button>
                <button className="btn-primary flex-1" onClick={sendInvite} disabled={submitting}>
                  <Send size={14} className="inline mr-1" />
                  {submitting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
